import { motion } from "framer-motion";
import { MainNav } from "../components/MainNav";
import { useLanguage } from "../contexts/LanguageContext";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEOHead from "../components/SEOHead";
import { getOrganizationSchema } from "../lib/structuredData";

const AboutPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const seoContent = {
    title: language === 'sv' 
      ? 'Om Petrobell - Marina drivmedel och bunkerleveranser'
      : 'About Petrobell - Marine Fuel and Bunker Deliveries',
    description: language === 'sv'
      ? 'Petrobell tror på ökad användning av sjövägarna för miljövänligare transporter. Vi fokuserar på säkerhet, effektivitet och miljöansvar i hela vår verksamhet.'
      : 'Petrobell believes in increased use of sea routes for more environmentally friendly transportation. We focus on safety, efficiency and environmental responsibility throughout our operations.',
    keywords: language === 'sv'
      ? 'om Petrobell, marina drivmedel, bunkerleveranser Sverige, miljövänliga transporter, sjötransport, fartygsbränsle'
      : 'about Petrobell, marine fuel, bunker deliveries Sweden, eco-friendly transport, sea transport, ship fuel'
  };

  const content = {
    title: language === 'sv' ? 'Om Petrobell' : 'About Petrobell',
    mainText: language === 'sv' 
      ? [
          'Petrobell tror att en ökad användning av sjövägarna tillhör framtiden. Skillnaden mellan att transportera större som mindre volymer med fartyg istället för tankbil på gatorna är betydande både för människa och miljö. För att på ett miljömässigt och säkert sätt verka efter vår egen vision genomför vi kontinuerligt tekniska uppdateringar av våra fartyg för att öka effektivitet samt minska utsläpp.',
          'Utmaningarna för Petrobell ligger i att kunna tillhandahålla lösningar på komplexa leveranser på varierande platser med sjönära läge. Ytterligare effektivisering och reducering av vår egen miljöpåverkan når vi genom att jobba med smarta system och digitala informationskällor för exempelvis pejlning av cisterner och tankar. Detta ger möjligheten att i större utsträckning optimera leveranserna så att få kilometer eller nautiska mil tillryggaläggs i onödan.',
          'Den faktor som vi dock alltid sätter högst upp på agendan är säkerhet. På inget sätt skall människa eller miljö behöva riskeras i anslutning till verksamheten.',
          'Genom Petrobells historia finns en stor uppbyggd kunskap kring verksamhet vid och drift av sjöstationer. Därigenom erbjuder Petrobell den kategorin av kunder stöd kring driftfrågor, kontroller och mycket mer.'
        ]
      : [
          'Petrobell believes that increased use of sea routes belongs to the future. The difference between transporting larger and smaller volumes by vessel instead of tank lorry on the streets is significant for both people and the environment. To operate in an environmentally friendly and safe manner according to our own vision, we continuously carry out technical updates of our vessels and ensure that they are powered by biofuels.',
          'The challenges for Petrobell lie in being able to provide solutions for complex deliveries at various locations with proximity to the sea. We achieve further efficiency and reduction of our own environmental impact by working with smart systems and digital information sources, for example for gauging cisterns and tanks. This provides the opportunity to optimise deliveries to a greater extent so that few kilometres or nautical miles are covered unnecessarily.',
          'However, the factor that we always put at the top of the agenda is safety. In no way should people or the environment need to be risked in connection with the operation.',
          'Throughout Petrobell\'s history, there is a great built-up knowledge about operations at and operation of sea stations. Through this, Petrobell offers that category of customers support with advice on operational issues, controls and much more.'
        ],
    ourVessels: language === 'sv' ? 'Våra Fartyg' : 'Our Vessels',
    viewVessels: language === 'sv' ? 'Se våra fartyg' : 'View our vessels'
  };

  return (
    <div className="min-h-screen w-full">
      <SEOHead
        title={seoContent.title}
        description={seoContent.description}
        path="/about"
        image="/images/stockholm.jpg"
        language={language}
        keywords={seoContent.keywords}
        jsonLd={getOrganizationSchema(language)}
      />
      <MainNav />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: "url('/images/stockholm.jpg')" }}
        role="img"
        aria-label={language === 'sv' ? 'Stockholms skärgård - Petrobells verksamhetsområde' : 'Stockholm archipelago - Petrobell operating area'}
      >
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => navigate('/')}
          className="absolute top-24 left-4 md:left-8 z-20 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          <span>{language === 'sv' ? 'Tillbaka' : 'Back'}</span>
        </motion.button>
        
        <div className="container mx-auto px-4 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1 mb-6 text-sm font-medium bg-white/20 backdrop-blur-sm rounded-full text-white">
              Petrobell
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {content.title}
            </h1>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* About Content Section */}
      <section className="relative min-h-screen flex items-center py-20 bg-[url('/lovable-uploads/13faa4c6-89fd-4ccd-b21f-9a5b9cb2cdf7.png')] bg-cover bg-center md:bg-fixed bg-no-repeat">
        <div className="absolute inset-0 bg-black/60" />
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center bg-black/40 backdrop-blur-sm rounded-2xl p-8 md:p-12"
            >
              <span className="inline-block px-4 py-1 mb-6 text-sm font-medium bg-white/20 backdrop-blur-sm rounded-full text-white">
                {content.title}
              </span>
              <div className="space-y-6 mb-8">
                {content.mainText.map((paragraph, index) => (
                  <motion.p 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    viewport={{ once: true }}
                    className="text-lg md:text-xl text-white/90 leading-relaxed"
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/vessels#vessels-hero"
                  className="inline-block px-10 py-4 text-lg bg-primary text-white rounded-lg font-medium transition-all hover:shadow-lg"
                >
                  {content.viewVessels}
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
